import {
  getVisibleApps,
  type UiProductType,
} from '@app/app-updates-product-app';
import { STATUS_IDS, CHANGE_TYPE_IDS } from '../constants';
import { UpdateStatus, ChangeType, Product } from '../types';

export interface FilterUrlParams {
  search?: string;
  products?: string;
  statuses?: string;
  changeTypes?: string;
  cursor?: string;
  limit?: string;
}

export interface FilterState {
  selectedProducts: Product[] | null;
  selectedStatuses: UpdateStatus[] | null;
  selectedChangeTypes: ChangeType[] | null;
  searchQuery: string | null;
  cursor?: string;
  limit?: number;
}

/**
 * Converts filter state to URL query parameters
 */
export const filtersToUrlParams = (filters: FilterState): FilterUrlParams => {
  const params: FilterUrlParams = {};

  if (filters.searchQuery) {
    params.search = filters.searchQuery;
  }

  if (filters.selectedProducts && filters.selectedProducts.length > 0) {
    params.products = filters.selectedProducts.map((p) => p.id).join(',');
  }

  if (filters.selectedStatuses && filters.selectedStatuses.length > 0) {
    params.statuses = filters.selectedStatuses.map((p) => p.id).join(',');
  }

  if (filters.selectedChangeTypes && filters.selectedChangeTypes.length > 0) {
    params.changeTypes = filters.selectedChangeTypes.map((p) => p.id).join(',');
  }

  if (filters.cursor) {
    params.cursor = filters.cursor;
  }

  if (typeof filters.limit === 'number' && Number.isFinite(filters.limit)) {
    params.limit = String(filters.limit);
  }

  return params;
};

// Product.id is UiProductType (e.g. 'JIRA_SOFTWARE') for consistent client-side representation.
// getVisibleApps() already returns keys as UiProductType (uppercase).
const availableProducts: Product[] = Object.entries(getVisibleApps()).map(
  ([key, config]) => ({
    id: key as UiProductType,
    name: config.displayName,
    icon: config.icon,
  }),
);

const availableStatuses: UpdateStatus[] = STATUS_IDS.map((statusId) => ({
  id: statusId,
  name: statusId,
}));

const availableChangeTypes: ChangeType[] = CHANGE_TYPE_IDS.map(
  (changeTypeId) => ({
    id: changeTypeId,
    name: changeTypeId,
  }),
);

/**
 * Converts URL query parameters to filter state
 */
export const urlParamsToFilters = (): Partial<FilterState> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);

  const filters: Partial<FilterState> = {};

  const search = params.get('search');
  if (search) {
    filters.searchQuery = search;
  }

  const products = params.get('products');
  if (products) {
    const productKeys = products.split(',').filter(Boolean);
    filters.selectedProducts = availableProducts.filter((p) =>
      productKeys.includes(p.id),
    );
  }

  const statuses = params.get('statuses');
  if (statuses) {
    const statusIds = statuses.split(',').filter(Boolean);
    filters.selectedStatuses = availableStatuses.filter((s) =>
      statusIds.includes(s.id),
    );
  }

  const changeTypes = params.get('changeTypes');
  if (changeTypes) {
    const changeTypeIds = changeTypes.split(',').filter(Boolean);
    filters.selectedChangeTypes = availableChangeTypes.filter((c) =>
      changeTypeIds.includes(c.id),
    );
  }

  const cursor = params.get('cursor');
  if (cursor) {
    filters.cursor = cursor;
  }

  const limit = params.get('limit');
  if (limit) {
    const parsedLimit = Number(limit);
    if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
      filters.limit = parsedLimit;
    }
  }

  return filters;
};

/**
 * Creates a clean URL search string from filter params
 */
export const createFilterUrlSearch = (params: FilterUrlParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const searchString = searchParams.toString();
  return searchString ? `?${searchString}` : '';
};
